import { AuthApiService } from "@app/shared/authentication/services/auth-api.service";
import { Component, inject, ViewChild, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { VerifyOtpFormComponent } from "@app/shared/authentication/components/verify-otp-form/verify-otp-form.component";
import { ButtonComponent } from "@kernel/shared/modules/button/components/button/button.component";
import { SxwFormComponent } from "@kernel/form/components/sxw-form/sxw-form.component";
import { CommonModule } from "@angular/common";
import { VerifyOtpFormModel } from "../../../../../../shared/authentication/models/verify-otp-form.model";
import { takeUntil, Subject, finalize } from "rxjs";
import { EOtpReason } from "@app/shared/authentication/enums/otp.enum";
import { SideDialogComponent } from "@app/kernel/popups/popups.config";
import { ProfileService } from "@app/applications/profile/services/profile.service";
import { MessageService } from "@app/kernel/tools/message/service/message.service";

@Component({
  selector: "app-verify-update-email",
  imports: [VerifyOtpFormComponent, TranslateModule, ButtonComponent, SxwFormComponent, CommonModule, VerifyOtpFormComponent, SideDialogComponent],
  templateUrl: "./verify-update-email.component.html",
  styleUrl: "./verify-update-email.component.scss",
  providers: [VerifyOtpFormModel, ProfileService, AuthApiService],
})
export class VerifyUpdateEmailComponent implements OnDestroy {
  @ViewChild(SideDialogComponent) sideDialog!: SideDialogComponent;

  isDialogVisible = true;
  readonly formModel = inject(VerifyOtpFormModel);
  private readonly router = inject(Router);
  private profileService = inject(ProfileService);
  private authApiService = inject(AuthApiService);
  private messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private destroy$ = new Subject<void>();
  private countdownIntervalId?: ReturnType<typeof setTimeout>;
  buttonLabel = "resendOtp.label";
  disabled = false;
  isLoading = false;

  /**
   * Verifies the One-Time Password (OTP) provided by the user.
   * If the form is valid, it sends the OTP for verification via the AuthApiService.
   * Updates the loading state during the process.
   */
  async verifyOtp(): Promise<void> {
    if (this.formModel.formStatus() !== "VALID") return;
    this.isLoading = true;
    this.profileService
      .verify2FA(this.formModel.getCurrentDtoInput())
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: res => {
          this.messageService.success(res?.text || this.translate.instant("EmailUpdatedsuccessfully"), this.translate.instant("success"));
          this.hideDialog();
          this.router.navigate(["/profile"], {
            queryParams: {
              reload: new Date().getTime(),
              emailUpdated: true,
            },
          });
        },
      });
  }

  /**
   * Triggers the resend OTP functionality with a specified reason
   * and starts a countdown timer for 60 seconds.
   */
  resendOtp(): void {
    this.authApiService
      .resendOtp(EOtpReason.CONFIRM_EMAIL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.startCountdown(60));
  }

  hideDialog() {
    this.isDialogVisible = false;
  }

  /**
   * Starts a countdown timer that updates the resend button label.
   * @param seconds - The countdown duration in seconds.
   */
  private startCountdown(seconds: number): void {
    let counter = seconds;
    this.disabled = true;
    this.updateButtonLabel(counter);

    this.clearCountdown(); // In case a previous timer exists
    this.countdownIntervalId = setInterval(() => {
      counter--;
      if (counter >= 0) {
        this.updateButtonLabel(counter);
      } else {
        this.resetResendButton();
      }
    }, 1000);
  }

  /**
   * Clears any active countdown interval.
   */
  private clearCountdown(): void {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = undefined;
    }
  }

  /**
   * Updates the resend button label with the countdown.
   * @param counter - Current seconds remaining.
   */
  private updateButtonLabel(counter: number): void {
    const label = this.translate.instant("resendOtp.label");
    this.buttonLabel = `${label} (0:${counter})`;
  }

  /**
   * Resets the resend button to its default state.
   */
  private resetResendButton(): void {
    this.clearCountdown();
    this.disabled = false;
    this.buttonLabel = this.translate.instant("resendTheCode");
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearCountdown();
  }
}
